import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Share2, Copy, Check, X, Clock, Download, Mail, StickyNote, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TTL_PRESETS = [
  { minutes: 30,          label: '30 min · quick send' },
  { minutes: 60 * 2,      label: '2 hours · same visit' },
  { minutes: 60 * 24,     label: '24 hours · overnight' },
  { minutes: 60 * 24 * 3, label: '3 days · weekend' },
  { minutes: 60 * 24 * 7, label: '7 days · full week (max)' },
];

/**
 * ShareFileModal — Iter 141 Family-Facing Share UI.
 *
 * Opens a two-step flow:
 *   1. Form — TTL, max downloads, optional recipient email + note
 *   2. Success — shows the download URL with a copy button + expiry
 *
 * The URL itself is the credential. Show a clear disclosure telling
 * the user "anyone with this link can download the file until it
 * expires or is revoked" so they treat the URL like a password.
 */
export default function ShareFileModal({ fileId, fileName, open, onClose, onShared }) {
  const { getAuthHeader } = useAuth();
  const [step, setStep] = useState('form');
  const [ttl, setTtl] = useState(TTL_PRESETS[2].minutes);
  const [maxDownloads, setMaxDownloads] = useState(1);
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('form');
      setResult(null);
      setCopied(false);
      setRecipient('');
      setNote('');
      setTtl(TTL_PRESETS[2].minutes);
      setMaxDownloads(1);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fileId) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/files/${fileId}/share`,
        {
          ttl_minutes: ttl,
          max_downloads: maxDownloads,
          recipient_email: recipient?.trim() || null,
          note: (note || '').slice(0, 200),
        },
        getAuthHeader(),
      );
      setResult(data);
      setStep('success');
      onShared?.(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not create share link. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.download_url) return;
    try {
      await navigator.clipboard.writeText(result.download_url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not access clipboard');
    }
  };

  const expiresPretty = result?.expires_at
    ? new Date(result.expires_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '—';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-file-modal-overlay"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-cn-border"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-file-modal"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-cn-border/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
              <Share2 className="w-4 h-4" />
            </div>
            <div>
              <div className="cn-display text-lg text-cn-text">Share document</div>
              {fileName && (
                <div className="text-xs text-cn-text-muted truncate max-w-[300px]">
                  {fileName}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-cn-bg-soft text-cn-text-muted flex items-center justify-center"
            aria-label="Close"
            data-testid="share-file-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 'form' && (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-cn-text-muted mb-2">
                <Clock className="w-3.5 h-3.5" /> Link expires
              </label>
              <div className="space-y-1.5" data-testid="share-ttl-options">
                {TTL_PRESETS.map((opt) => (
                  <label
                    key={opt.minutes}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors
                      ${ttl === opt.minutes
                        ? 'border-cn-primary bg-cn-primary/5 text-cn-text'
                        : 'border-cn-border/60 text-cn-text-muted hover:bg-cn-bg-soft'}`}
                  >
                    <input
                      type="radio"
                      name="ttl"
                      value={opt.minutes}
                      checked={ttl === opt.minutes}
                      onChange={() => setTtl(opt.minutes)}
                      className="accent-cn-primary"
                      data-testid={`share-ttl-${opt.minutes}`}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-cn-text-muted mb-2">
                <Download className="w-3.5 h-3.5" /> How many times can it be downloaded?
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={maxDownloads}
                  onChange={(e) => setMaxDownloads(parseInt(e.target.value, 10))}
                  className="flex-1 accent-cn-primary"
                  data-testid="share-max-downloads-range"
                />
                <span
                  className="text-sm font-mono text-cn-text w-16 text-right"
                  data-testid="share-max-downloads-value"
                >
                  {maxDownloads === 1 ? '1 (single-use)' : `${maxDownloads}×`}
                </span>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-cn-text-muted mb-2">
                <Mail className="w-3.5 h-3.5" /> Recipient email <span className="text-cn-text-muted/60">(optional, audit only)</span>
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="family@example.com"
                className="w-full px-3 py-2 border border-cn-border rounded-lg text-sm focus:outline-none focus:border-cn-primary bg-white"
                data-testid="share-recipient-email"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-cn-text-muted mb-2">
                <StickyNote className="w-3.5 h-3.5" /> Note for the audit log <span className="text-cn-text-muted/60">(optional)</span>
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 200))}
                placeholder="For John's discharge nurse"
                maxLength={200}
                className="w-full px-3 py-2 border border-cn-border rounded-lg text-sm focus:outline-none focus:border-cn-primary bg-white"
                data-testid="share-note"
              />
              <div className="text-[10px] text-cn-text-muted/70 mt-1 text-right">
                {note.length}/200
              </div>
            </div>

            <div className="text-[11px] text-cn-text-muted bg-amber-50 border border-amber-200 rounded-lg p-3">
              <b>Security note:</b> anyone with this link can download the file until
              it expires or you revoke it. Treat the URL like a password.
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm text-cn-text-muted hover:bg-cn-bg-soft"
                data-testid="share-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm bg-cn-primary text-white font-medium hover:bg-cn-primary/90 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="share-submit-btn"
              >
                <Link2 className="w-3.5 h-3.5" />
                {submitting ? 'Creating…' : 'Create share link'}
              </button>
            </div>
          </form>
        )}

        {step === 'success' && result && (
          <div className="px-6 py-5 space-y-4" data-testid="share-file-modal-success">
            <div className="text-sm text-cn-text">
              <div className="cn-display text-lg mb-1">Link ready to send</div>
              <div className="text-cn-text-muted text-xs">
                Expires {expiresPretty} · {result.max_downloads} download
                {result.max_downloads !== 1 ? 's' : ''} allowed
              </div>
            </div>

            <div className="flex items-stretch gap-2">
              <div className="flex-1 bg-cn-bg-soft border border-cn-border rounded-lg px-3 py-2 text-xs font-mono break-all">
                {result.download_url}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className={`px-4 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                  copied ? 'bg-emerald-600 text-white' : 'bg-cn-primary text-white hover:bg-cn-primary/90'
                }`}
                data-testid="share-copy-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="text-[11px] text-cn-text-muted bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              This link + expiry are audit-logged. You can revoke it any time from
              <b> /me/shares</b>.
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm bg-cn-primary text-white font-medium hover:bg-cn-primary/90"
                data-testid="share-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
